import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  Toast,
  ToastTitle,
  tokens,
  useToastController,
} from "@fluentui/react-components";
import { ArrowSync20Regular, Eye20Regular } from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  getEmailParcelCandidateDetails,
  refetchEmailParcels,
  setSelectedParcelOnEmail,
} from "@/api/emails";
import {
  getDocumentParcelCandidateDetails,
  refetchDocumentParcels,
  setSelectedParcelOnDocument,
} from "@/api/documents";
import { ApiError } from "@/lib/apiClient";
import { ParcelPreviewModal } from "@/features/review/ParcelPreviewModal";
import type { ParcelSummary } from "@/types/domain";

interface ParcelSelectorProps {
  /** Email message_id_hash for refetch + email-scope writes. */
  emailId: string;
  /** Document UUID, when invoked from the doc-detail surface. ``null`` on
   * the email-detail drawer — the scope toggle hides "this document only". */
  documentId: string | null;
  selectedParcelNumber: number | null;
  parcelCandidates: ParcelSummary[];
  parcelFetchStatus: string | null;
  open: boolean;
  onClose: () => void;
  toasterId: string;
}

type Scope = "document" | "email";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

function fetchStatusBadge(
  status: string | null,
): { color: "success" | "warning" | "danger" | "informative"; label: string } {
  switch (status) {
    case "success":
      return { color: "success", label: "candidates resolved" };
    case "failed":
      return { color: "danger", label: "Symphony unreachable" };
    case "pending":
      return { color: "warning", label: "not yet resolved" };
    default:
      return { color: "informative", label: status ?? "unknown" };
  }
}

function extractSymphonyError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as Record<string, unknown>).detail;
      if (typeof detail === "string") return detail;
      if (detail && typeof detail === "object" && "detail" in detail) {
        const inner = (detail as Record<string, unknown>).detail;
        if (typeof inner === "string") return inner;
      }
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Request failed";
}

export function ParcelSelector({
  emailId,
  documentId,
  selectedParcelNumber,
  parcelCandidates,
  parcelFetchStatus,
  open,
  onClose,
  toasterId,
}: ParcelSelectorProps) {
  const qc = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);

  // Hydrate the slim DB candidate list with fresh Symphony metadata
  // (trade key, qty, delivery dates) only while the modal is open, so
  // we don't pay the Symphony round-trip on every detail load.
  const detailsQuery = useQuery<ParcelSummary[]>({
    queryKey:
      documentId !== null
        ? ["parcel-candidate-details", "doc", documentId]
        : ["parcel-candidate-details", "email", emailId],
    queryFn: () =>
      documentId !== null
        ? getDocumentParcelCandidateDetails(documentId)
        : getEmailParcelCandidateDetails(emailId),
    enabled: open && parcelCandidates.length > 0,
    staleTime: 30_000,
  });

  // Prefer the enriched list when it has loaded; fall back to the slim
  // DB list while loading or if the request failed.
  const candidates: ParcelSummary[] =
    detailsQuery.data && detailsQuery.data.length > 0
      ? detailsQuery.data
      : parcelCandidates;
  const fetchStatus = parcelFetchStatus;
  const showRefetch =
    fetchStatus === "failed" ||
    fetchStatus === "pending" ||
    candidates.length === 0;
  const status = fetchStatusBadge(fetchStatus);

  // Email-only scope when the caller has no document context (drawer).
  const [scope, setScope] = useState<Scope>(
    documentId !== null ? "document" : "email",
  );
  const [error, setError] = useState<string | null>(null);
  const [previewNumber, setPreviewNumber] = useState<number | null>(null);

  const refetchMutation = useMutation<unknown, Error, void>({
    // Per-doc endpoint when we have a document context — it falls back
    // to the email's trade and also handles the override-only case
    // where the email itself has no trade key.
    mutationFn: () =>
      documentId !== null
        ? refetchDocumentParcels(documentId)
        : refetchEmailParcels(emailId),
    onSuccess: () => {
      setError(null);
      if (documentId !== null) {
        void qc.invalidateQueries({ queryKey: ["document", documentId] });
      }
      void qc.invalidateQueries({ queryKey: ["email", emailId] });
      void qc.invalidateQueries({ queryKey: ["parcel-candidate-details"] });
    },
    onError: (err) => setError(extractSymphonyError(err)),
  });

  const selectMutation = useMutation<unknown, Error, number>({
    mutationFn: async (parcelNumber: number) => {
      if (scope === "document" && documentId !== null) {
        return setSelectedParcelOnDocument(documentId, parcelNumber);
      }
      return setSelectedParcelOnEmail(emailId, parcelNumber);
    },
    onSuccess: () => {
      if (documentId !== null) {
        void qc.invalidateQueries({ queryKey: ["document", documentId] });
      }
      void qc.invalidateQueries({ queryKey: ["email", emailId] });
      void qc.invalidateQueries({ queryKey: ["emails"] });
      dispatchToast(
        <Toast>
          <ToastTitle>Parcel selected</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      onClose();
    },
    onError: (err) => setError(extractSymphonyError(err)),
  });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(_, d) => {
          if (!d.open) onClose();
        }}
      >
      <DialogSurface style={{ maxWidth: 720 }}>
        <DialogBody>
          <DialogTitle>Select parcel</DialogTitle>
          <DialogContent>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <Badge appearance="tint" color={status.color}>
                {status.label}
              </Badge>
              {selectedParcelNumber !== null ? (
                <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                  Currently selected: parcel {selectedParcelNumber}
                </Text>
              ) : null}
            </div>

            {documentId !== null ? (
              <Field
                label="Apply selection to"
                hint="Document overrides the email-level pick. Email pick applies to every doc on the email that has no override."
                style={{ marginBottom: 12 }}
              >
                <RadioGroup
                  value={scope}
                  onChange={(_, data) => setScope(data.value as Scope)}
                  layout="horizontal"
                >
                  <Radio value="document" label="This document only" />
                  <Radio value="email" label="Whole email (default)" />
                </RadioGroup>
              </Field>
            ) : null}

            {error !== null ? (
              <MessageBar intent="error" style={{ marginTop: 12 }}>
                <MessageBarBody>
                  <MessageBarTitle>Error</MessageBarTitle>
                  {error}
                </MessageBarBody>
              </MessageBar>
            ) : null}

            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text size={300} weight="semibold">
                    Candidates ({candidates.length})
                  </Text>
                  {detailsQuery.isFetching ? <Spinner size="tiny" /> : null}
                </div>
                <Button
                  size="small"
                  icon={
                    refetchMutation.isPending ? (
                      <Spinner size="tiny" />
                    ) : (
                      <ArrowSync20Regular />
                    )
                  }
                  disabled={refetchMutation.isPending}
                  onClick={() => refetchMutation.mutate()}
                >
                  {refetchMutation.isPending
                    ? "Fetching…"
                    : "Refetch from Symphony"}
                </Button>
              </div>
              {showRefetch ? (
                <Text
                  size={200}
                  style={{ color: tokens.colorNeutralForeground2 }}
                >
                  {fetchStatus === "failed"
                    ? "Last attempt failed; retry."
                    : "No candidates yet — query Symphony."}
                </Text>
              ) : null}
              <ParcelList
                parcels={candidates}
                emptyText="No candidates resolved for this email."
                onSelect={(p) => selectMutation.mutate(p.parcelNumber)}
                onPreview={(p) => setPreviewNumber(p.parcelNumber)}
                selectingNumber={
                  selectMutation.isPending
                    ? selectMutation.variables ?? null
                    : null
                }
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    {previewNumber !== null ? (
      <ParcelPreviewModal
        parcelNumber={previewNumber}
        open
        onClose={() => setPreviewNumber(null)}
      />
    ) : null}
    </>
  );
}

interface ParcelListProps {
  parcels: ParcelSummary[];
  emptyText: string;
  onSelect: (p: ParcelSummary) => void;
  onPreview: (p: ParcelSummary) => void;
  selectingNumber: number | null;
}

function ParcelList({
  parcels,
  emptyText,
  onSelect,
  onPreview,
  selectingNumber,
}: ParcelListProps) {
  if (parcels.length === 0) {
    return (
      <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
        {emptyText}
      </Text>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {parcels.map((p) => (
        <div
          key={p.parcelNumber}
          style={{
            border: `1px solid ${tokens.colorNeutralStroke2}`,
            borderRadius: 4,
            padding: 8,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Text size={300} weight="semibold">
              Parcel {p.parcelNumber}
              {p.parcelStatus !== null && p.parcelStatus !== undefined ? (
                <Badge
                  appearance="tint"
                  color="informative"
                  style={{ marginLeft: 8 }}
                >
                  status {p.parcelStatus}
                </Badge>
              ) : null}
            </Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
              Trade {p.tradeNumber ?? "?"}.{p.orderNumber ?? "?"}.
              {p.itemNumber ?? "?"}
              {" · qty "}
              {p.nominatedQuantity ?? "—"}
              {" · "}
              {formatDate(p.deliveryFromDate)} → {formatDate(p.deliveryEndDate)}
            </Text>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              icon={<Eye20Regular />}
              onClick={() => onPreview(p)}
              title="Preview Symphony state for this parcel"
              aria-label={`Preview parcel ${p.parcelNumber}`}
            />
            <Button
              appearance="primary"
              disabled={selectingNumber !== null}
              onClick={() => onSelect(p)}
            >
              {selectingNumber === p.parcelNumber ? "Selecting…" : "Select"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
